import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api, { API, formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Settings2 } from "lucide-react";

export default function AdminTemplates() {
  const [templates, setTemplates] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [lang, setLang] = useState("en");
  const [defaultMsg, setDefaultMsg] = useState("");
  const fileRef = useRef(null);

  const load = () => Promise.all([api.get("/templates"), api.get("/languages")]).then(([t, l]) => {
    setTemplates(t.data); setLanguages(l.data);
  });
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Pick a template image");
    if (!name) return toast.error("Enter template name");
    try {
      const fd = new FormData();
      fd.append("name", name); fd.append("language_code", lang); fd.append("default_message", defaultMsg);
      fd.append("file", file);
      await api.post("/admin/templates", fd);
      toast.success("Template uploaded");
      setOpen(false); setName(""); setDefaultMsg(""); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/admin/templates/${id}`); load();
  };

  return (
    <Shell role="admin">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Templates</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Poster Templates</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="h-11 bg-brand-600 hover:bg-brand-700 gap-2" data-testid="btn-add-template"><Plus className="w-4 h-4"/>Upload Template</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Upload Template</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider">Name</Label><Input value={name} onChange={(e)=>setName(e.target.value)} data-testid="tpl-name"/></div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">Language</Label>
                <Select value={lang} onValueChange={setLang}>
                  <SelectTrigger data-testid="tpl-lang"><SelectValue/></SelectTrigger>
                  <SelectContent>{languages.map(l => <SelectItem key={l.id} value={l.code}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider">Default Message</Label><Textarea value={defaultMsg} onChange={(e)=>setDefaultMsg(e.target.value)} className="min-h-[100px]" data-testid="tpl-default-msg"/></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider">Artwork (PNG/JPG)</Label><input ref={fileRef} type="file" accept="image/png,image/jpeg" data-testid="tpl-file" className="block text-sm"/></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={upload} className="bg-brand-600 hover:bg-brand-700" data-testid="tpl-save">Upload</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {templates.map((t) => (
          <Card key={t.id} className="overflow-hidden" data-testid={`tpl-card-${t.id}`}>
            <div className="aspect-[1/1.4] bg-slate-100">
              <img src={`${API}/templates/${t.id}/file`} alt={t.name} className="w-full h-full object-contain bg-white" />
            </div>
            <div className="p-4 space-y-2">
              <div className="font-semibold text-slate-900">{t.name}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">{t.language_code}</div>
              <p className="text-xs text-slate-600 line-clamp-2">{t.default_message}</p>
              <div className="flex gap-2 pt-2">
                <Link to={`/admin/templates/${t.id}/positions`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full gap-2" data-testid={`tpl-positions-${t.id}`}><Settings2 className="w-3.5 h-3.5"/>Positions</Button>
                </Link>
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(t.id)} data-testid={`tpl-delete-${t.id}`}><Trash2 className="w-4 h-4"/></Button>
              </div>
            </div>
          </Card>
        ))}
        {templates.length === 0 && <Card className="p-8 col-span-full text-center text-slate-500 border-dashed">No templates yet.</Card>}
      </div>
    </Shell>
  );
}
